import * as React from 'react'
import { AutocompleteInput, Item, Switch, ROLE_NAME, Title, $Text, ROLES, ROLE_ORIGIN, DataTable, FILTER_TYPE, DataRow, NewModal, NotificationType, NewCheckbox, ConfirmAction, Tooltip, SvgIcon, NewButton, USER_STATUS, newTheme, NewTabs } from 'prace-common-components'
import { $TabsContainer, $Container, $ListsContainer, $ModalContainer, $NameColumn, $SwitchContainer } from './styles'
import Grid from '@material-ui/core/Grid'
import { EditCutoffRoleProps } from './types'
import { UsersListModal } from 'components/UsersListModal'
import { useCreateUsersGroupMutation, useUpdateUsersGroupMutation, useDeleteUsersGroupMutation } from 'store/api/users'
import { useAppDispatch } from 'store/hooks'
import { useCreateCutOffRolesMutation, useRemoveCutOffRolesMutation, useUpdateCallRoleMutation, useUpdateCutOffRoleMutation } from 'store/api/calls'
import { getCurrentCutoff } from 'util/getCurrentCutoff'
import { DefaultRoleModal } from './DefaultRoleModal'
import { UserRole } from 'store/api/calls/types'

const NotEditableRoles = [ROLES.COMPUTE_CENTRE_REPRESENTATIVE, ROLES.TECHNICAL_REVIEWER, ROLES.APPLICANT]

export const EditCutoffRoleModal: React.FC<EditCutoffRoleProps> = ({
  refetch,
  callId,
  setCallRoles,
  getCutOffRoles,
  userGroups,
  roles,
  role,
  roleLists,
  allCutoffRoles,
  userGroupItems,
  handleClose,
  cutoffs,
  centers,
}) => {
  const dispatch = useAppDispatch()
  const initialCutoffId = getCurrentCutoff(cutoffs).id || 0
  const [selectedCutoffId, setSelectedCutoffId] = React.useState<number>(initialCutoffId)
  const selectedCutoffIdRef = React.useRef<number>(initialCutoffId)
  const [createUserGroup] = useCreateUsersGroupMutation()
  const [updateUserGroup] = useUpdateUsersGroupMutation()
  const [deleteUserGroup] = useDeleteUsersGroupMutation()
  const [createUserRoles] = useCreateCutOffRolesMutation()
  const [removeUserRoles] = useRemoveCutOffRolesMutation()
  const [updateCutOffRole] = useUpdateCutOffRoleMutation()
  const [updateCallRole] = useUpdateCallRoleMutation()

  const [createUserListOpen, setCreateUserListOpen] = React.useState<ROLES | undefined>()
  const [createDefaultUserListOpen, setCreateDefaultUserListOpen] = React.useState<ROLES | undefined>()
  const [customUserListOpen, setCustomUserListOpen] = React.useState<ROLES | undefined>()
  const [editUserListOpen, setEditUserListOpen] = React.useState<StringNumber | undefined>()
  const [editDefaultUserListOpen, setEditDefaultUserListOpen] = React.useState<StringNumber | undefined>()
  const [changeDefaultUserListOpen, setChangeDefaultUserListOpen] = React.useState<StringNumber | undefined>()

  React.useEffect(() => { selectedCutoffIdRef.current = selectedCutoffId }, [selectedCutoffId])

  const notEditable = NotEditableRoles.includes(role)

  const isCCR = role === ROLES.COMPUTE_CENTRE_REPRESENTATIVE
  const isTechnicalReviewer = role === ROLES.TECHNICAL_REVIEWER

  const cutOffRoles = allCutoffRoles ? 
    allCutoffRoles[role]?.filter((r) => r.cutoff === selectedCutoffId) : []
                                
  const hasRoles = cutOffRoles?.length
  const assignRoles = hasRoles ? cutOffRoles.filter((r) => r.origin == ROLE_ORIGIN.ASSIGNMENT) : []
  const allAssignRoles = allCutoffRoles && hasRoles ?
    allCutoffRoles[role].filter((r) => r.origin == ROLE_ORIGIN.ASSIGNMENT) : []

  const allForcedRoles = allCutoffRoles && hasRoles ? 
    allCutoffRoles[role].filter((r) => r.origin == ROLE_ORIGIN.MANUAL) : []
  const forcedRoles = hasRoles ? 
    cutOffRoles.filter((r) => r.origin == ROLE_ORIGIN.MANUAL) : []

  const cutoffRoleList = roleLists.find((list) => list.role === role && (list.cutoffId === selectedCutoffId))
  const defaultRoleList = roleLists.find((list) => list.role === role && !list.cutoffId)

  /* Roles in the user list but not actively assumming the role */
  const notActiveRoles = (cutoffRoleList?.userGroup?.users.filter(
    (u) => ![...forcedRoles, ...assignRoles].map((r) => r.user).includes(u.id)) || [])
    .map((r) => ({ 
      ...r,
      user: r.id,
      hasAssignments: false,
    })) || []
  const allNotActiveRoles = roleLists.filter((list) => list.role === role)
    .flatMap((list) => list.userGroup?.users.filter(
      (u) => ![...allForcedRoles, ...allAssignRoles].map((r) => r.user).includes(u.id)) || [])
    .map((r) => ({
      ...r,
      user: r.id,
      hasAssignments: false,
    })) || []

  const uniqueRoles = [...new Set([
    ...forcedRoles.map((r) => r.id),
    ...assignRoles.map((r) => r.id),
    ...notActiveRoles.map((r) => r.id),
  ])]
  const allUniqueRoles = [...new Set([
    ...allForcedRoles.map((r) => r.id),
    ...allAssignRoles.map((r) => r.id),
    ...allNotActiveRoles.map((r) => r.id),
  ])]

  const userRows = uniqueRoles.map(
    (id) => [...forcedRoles, ...notActiveRoles, ...assignRoles].find((r) => r.id === id)!)
  const allUserRows = allUniqueRoles.map(
    (id) => [...allForcedRoles, ...allNotActiveRoles, ...allAssignRoles].find((r) => r.id === id)!)

  const mapUserRow = (userRole: UserRole | any, centerName = '') => ({
    id: userRole.id,
    status: userRole.status,
    user: userRole.user,
    name: `${userRole.firstName} ${userRole.lastName}`,
    email: userRole.email || '',
    centerName,
    hasAssignments: userRole.hasAssignments,
    locked: !!forcedRoles.find((r) => r.user === userRole.user),
    otherRole: roles.find((s) => (allCutoffRoles || {})[s]?.find(
      (c) => (c.user === userRole.user) && (c.cutoff === selectedCutoffId) && s !== role)),
  })

  const rows = isCCR ?
    centers.map((c) => ({
      centerName: c.name,
      status: USER_STATUS.Active,
      id: c.id,
      user: c.user.id,
      name: `${c.user.firstName} ${c.user.lastName}`,
      email: c.user.email,
      hasAssignments: true,
      locked: true,
    })).sort((a, b) => a.name.localeCompare(b.name)) :
    isTechnicalReviewer ? (() => {
      /* Only centers with at least one partition allocated to the selected cutoff */
      const selectedCutoff = cutoffs.find((c) => c.id === selectedCutoffId)
      const cutoffCenterIds = new Set(
        (selectedCutoff?.resources || []).map((r) => r.partition?.centerId).filter(Boolean))
      const allCenterReviewers = centers.filter((c) => cutoffCenterIds.has(c.id)).flatMap((c) =>
        c.reviewers.map((r) => ({ ...r, centerName: c.name })),
      )
      const uniqueCenterReviewers = allCenterReviewers.filter(
        (r, i, arr) => arr.findIndex((x) => x.id === r.id) === i,
      )
      const centerReviewerIds = new Set(uniqueCenterReviewers.map((r) => r.id))

      const mergedRows = uniqueCenterReviewers.map((reviewer) => {
        const userRole = userRows.find((r) => r.user === reviewer.id)
        return {
          id: userRole?.id ?? 0,
          status: userRole?.status ?? USER_STATUS.Active,
          user: reviewer.id,
          name: `${reviewer.firstName} ${reviewer.lastName}`,
          email: reviewer.email,
          centerName: reviewer.centerName,
          hasAssignments: userRole?.hasAssignments ?? false,
          locked: !!forcedRoles.find((r) => r.user === reviewer.id),
          otherRole: roles.find((s) => (allCutoffRoles || {})[s]?.find(
            (c) => (c.user === reviewer.id) && (c.cutoff === selectedCutoffId) && s !== role)),
        }
      })

      const orphanRows = userRows
        .filter((r) => !centerReviewerIds.has(r.user))
        .filter((r, i, arr) => arr.findIndex((x) => x.user === r.user) === i)
        .map((userRole) => mapUserRow(userRole))

      return [...mergedRows, ...orphanRows].sort((a, b) => a.name.localeCompare(b.name))
    })() :
      userRows.map((userRole) => mapUserRow(userRole)).sort((a, b) => a.name.localeCompare(b.name))

  /* If every user is forced, then the role is locked */
  const allCutoffLocked = (!!forcedRoles.length && forcedRoles.length >= (rows.length || 0))
  const allLocked = (!!allForcedRoles.length && allForcedRoles.length >= (allUserRows.length || 0))
  
  const editingUserGroup = userGroups?.find((group) => group.id === editUserListOpen)
  const editingDefaultUserGroup = userGroups?.find((group) => group.id === editDefaultUserListOpen)

  const usersGroupName = cutoffRoleList?.userGroup?.name || ''
  const usersGroupId = cutoffRoleList?.userGroup?.id

  const defaultUsersGroupName = defaultRoleList?.userGroup?.name || ''
  const defaultUsersGroupId = defaultRoleList?.userGroup?.id

  const handleCreateUserGroup = async (
    name: string, users: number[], isDefault?: boolean,
  ) => {
    try {
      if(!users.length) return
      const newUserGroup = await createUserGroup({ name, users }).unwrap()
      /* We do not change the default user group here */
      if(!isDefault) {
        await onChangeUserGroup(newUserGroup.id)
      }
      await refetch()
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'User Pool created' } })
      setCreateUserListOpen(undefined)
      setCreateDefaultUserListOpen(undefined)
    } catch (err) {
      console.log(err)
    }
  }

  const handleEditUserGroup = async (name: string, users: number[], id?: number) => {
    try {
      if(!id || !users.length) return
      await updateUserGroup({ id, name, users }).unwrap()
      await refetch()
      const callRoles = await getCutOffRoles()
      setCallRoles(callRoles)
      setEditUserListOpen(undefined)
      setEditDefaultUserListOpen(undefined)
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'User Pool updated' } })
    } catch (err) {
      console.log(err)
    }
  }

  const handleDeleteUserGroup = async (id: number) => {
    try {
      if(!id) return
      await deleteUserGroup(id).unwrap()
      await refetch()
      const callRoles = await getCutOffRoles()
      setCallRoles(callRoles)
      setEditUserListOpen(undefined)
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'User List removed' } })
    } catch (err) {
      console.log(err)
    }
  }

  const onChangeDefaultUserGroup = async (userGroupId?: number, changeEveryCutoff = false) => {
    try {
      /* Check if we can select this user group */
      if(userGroupId && roles.some((r) => {
        const cutoffRoleList = roleLists.find((list) => list.role === r)
        return cutoffRoleList?.usersGroupId === userGroupId && r !== role
      })) {
        dispatch({ type: 'notification', payload: { type: NotificationType.error, msg: 'This user pool is already selected for this cutoff' } })
        return
      }
      await updateCallRole(
        { id: callId, roles: [{role, userGroupId: userGroupId ?? undefined}], changeEveryCutoff }).unwrap()
      await refetch()
      const callRoles = await getCutOffRoles()
      setCallRoles(callRoles)
      setChangeDefaultUserListOpen(undefined)
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Default user pool updated' } })
    } catch (err) {
      console.log(err)
    }
  }

  const onChangeUserGroup = async (userGroupId?: number) => {
    try {
      /* Check if we can select this user group */
      if(userGroupId && roles.some((r) => {
        const cutoffRoleList = roleLists.find((list) => list.role === r)
        return cutoffRoleList?.usersGroupId === userGroupId && r !== role
      })) {
        dispatch({ type: 'notification', payload: { type: NotificationType.error, msg: 'This user list is already selected for this cutoff' } })
        return
      }
      await updateCutOffRole(
        { id: selectedCutoffId, roles: [{role, userGroupId: userGroupId ?? undefined}] }).unwrap()
      await refetch()
      const callRoles = await getCutOffRoles()
      setCallRoles(callRoles)
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'User pool updated' } })
    } catch (err) {
      console.log(err)
    }
  }

  const handleLockUserRole = async (userId: number, prevLocked: boolean, hasAssignments: boolean, roleId: number) => {
    try{
      const cutOffId = selectedCutoffIdRef.current
      /* Create or lock User role */
      if(!prevLocked) {
        await createUserRoles({
          cutOffId,
          roles: [{
            userId,
            callId,
            cutOffId,
            role,
            origin: ROLE_ORIGIN.MANUAL,
          }],
        }).unwrap()
      } else  { /* Previously locked */
        if(hasAssignments) { /* Update the role to origin Assignment */
          await createUserRoles({
            cutOffId,
            roles: [{
              userId,
              callId,
              cutOffId,
              role,
              origin: ROLE_ORIGIN.ASSIGNMENT,
            }],
          }).unwrap()
        } else { /* The user will be unlocked and has no assignments */
          await removeUserRoles([roleId]).unwrap()
        } 
      }
      const callRoles = await getCutOffRoles()
      setCallRoles(callRoles)
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: prevLocked ? 'User role unlocked' : 'User role locked' } }) 
    } catch (err) {
      console.log(err)
    }
  }

  const onChangeCutoffLockAll = async (
    allCutoffLocked: boolean,
    userIds: number[],
    roleIds: number[],
    cutOffId: number,
    noNotification?: boolean,
  ) => {
    if(allCutoffLocked) {
      const roles = userIds.map((userId) => ({
        userId,
        callId,
        cutOffId,
        role,
        origin: ROLE_ORIGIN.MANUAL,
      }))
      await createUserRoles({
        cutOffId,
        roles,
      }).unwrap()
    } else {
      await removeUserRoles(roleIds).unwrap()
    }
    if(!noNotification) {
      const callRoles = await getCutOffRoles()
      setCallRoles(callRoles)
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: allLocked ? 'User roles locked' : 'User roles unlocked' } }) 
    }
  }
    
  /* Will Lock all roles from all cutoffs */
  const onChangeLockAll = async (allLocked: boolean, userIds: number[], roleIds: number[]) => {
    if(allLocked) {
      for(const cutOffId of cutoffs.map((c) => c.id || 0)) {
        await onChangeCutoffLockAll(allLocked, userIds, [], cutOffId, true)
      }
    } else {
      await removeUserRoles(roleIds).unwrap()
    }
    const callRoles = await getCutOffRoles()
    setCallRoles(callRoles)
    dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: allLocked ? 'User roles locked' : 'User roles unlocked' } }) 
  }

  const onClose = () => {
    setCreateUserListOpen(undefined)
    setCreateDefaultUserListOpen(undefined)
    setEditUserListOpen(undefined)
    setEditDefaultUserListOpen(undefined)
  }


  const columns = [
    { key: 'name', noSort: true, type: FILTER_TYPE.NONE, name: 'Name',
      formatter(props: { row: DataRow }) {
        const { name, status, email, otherRole } = props.row
        const blocked = status && status !== USER_STATUS.Active
        return <$NameColumn disabled={otherRole}>
          <Tooltip show text={otherRole ? `${email} - This user is a ${otherRole}` : blocked ? 'This user is blocked' : email} arrow placement='top'>
            <Grid item container alignItems='center' wrap='nowrap'>
              {otherRole || blocked ? <SvgIcon name='warning' color={newTheme.colors.feedback.error} /> : null}
              {name}
            </Grid>
          </Tooltip>
        </$NameColumn>
      },
    },
    ...(isCCR || isTechnicalReviewer ? [
      { key: 'centerName', noSort: true, type: FILTER_TYPE.NONE, name: 'Center', width: 100 },
    ] : []),
    ...(isCCR ? [] 
      : [{ key: 'hasAssignments', noSort: true, type: FILTER_TYPE.NONE, name: 'Assignment', width: 100,
        formatter(props: { row: DataRow }) {
          const { hasAssignments } = props.row
          return hasAssignments ?
            <$SwitchContainer container alignItems='center' justifyContent='center'>
              <SvgIcon name='check' />
            </$SwitchContainer>
            : null
        },
      }]),
    { key: 'locked', noSort: true, type: FILTER_TYPE.NONE, name: 'Locked', width: 70,
      formatter(props: { row: DataRow }) {
        const { hasAssignments, locked, id, user, otherRole } = props.row
        const disabled = hasAssignments || otherRole

        return <$SwitchContainer container alignItems='center' justifyContent='center'>
          {(disabled && !locked) || isCCR ? 
            <NewCheckbox disabled name='locked' value={hasAssignments || isCCR} title='' hideItalic/>
            :
            <ConfirmAction 
              title={locked ? 'Unlock user role' : 'Lock user role'} description='' 
              onConfirm={() => handleLockUserRole(user, locked, hasAssignments, id)}
            >
              <NewCheckbox disabled={locked ? false : disabled} name='locked' value={locked} title='' hideItalic />
            </ConfirmAction>
          }
        </$SwitchContainer>
      },
    },
  ]

  const cutoffOptions = cutoffs?.map((cutoff) => ({ id: cutoff.id || 0, label: cutoff.title || '' })) || []
  const selectedCutoffIdx = cutoffs.findIndex((c) => c.id === selectedCutoffId)

  return <$ModalContainer>
    <Title hasBack
      icon='arrow-left'
      iconProps={{ size: '20px' }}
      onBackClick={handleClose}>{ROLE_NAME[role]}</Title>
    <$Container container>
      <Title noMargin alternate>
        Assignee user pool
      </Title>
      {isCCR || isTechnicalReviewer ?
        <>
          <$Text>{`The ${ROLE_NAME[role]} role is defined in the Partition Manager.`}</$Text>
          <NewButton onClick={() => window.open('/pm', '_blank')}>
            Go to Partition Manager
          </NewButton>
        </>
        :
        (notEditable ? null : <>
          <$Text>Select, create or edit a list of users that can be assigned to this role.</$Text>
          <Grid item xs={12}>
            <AutocompleteInput
              name='user group'
              withNone
              disabled={notEditable}
              title='Default user pool'
              description='All cutoffs will be associated with this user pool (including future ones)'
              items={userGroupItems}
              onChange={(_, item) => setChangeDefaultUserListOpen((item as Item).value as number)}
              value={{ label: defaultUsersGroupName, value: defaultUsersGroupId } as Item}
              onCreateItem={() => setCreateDefaultUserListOpen(role)}
              onEditItem={setEditDefaultUserListOpen}
            /> 
          </Grid>
        </>)}
      <$TabsContainer>
        {!notEditable && <Grid item>
          <ConfirmAction
            title={allLocked ? 'Unlock all user roles from all cutoffs' : 'Lock all users to role from all cutoffs'} description=''
            onConfirm={() => 
              onChangeLockAll(!allLocked, allUserRows.map((r) => r.user), allForcedRoles.map((r) => r.id))}
          >
            <Switch 
              name='lock'
              title='Lock all users to role'
              value={allLocked}
              onChange={() => null}
            />
          </ConfirmAction>
        </Grid>}
        <NewTabs
          tabs={cutoffOptions}
          value={selectedCutoffIdx}
          scrollable
          onChange={(t: number) => setSelectedCutoffId(cutoffs.find((_, i) => i === t)?.id || 0)}
        />
        {(!customUserListOpen && (!usersGroupId || (defaultUsersGroupId === usersGroupId)) && !allCutoffLocked) ?
          <NewButton
            creator
            disabled={notEditable}
            onClick={() => setCustomUserListOpen(role)}
          >
            {`Custom user pool for cutoff ${cutoffOptions[selectedCutoffIdx]?.label || ''}`}
          </NewButton> :
          <>
            <AutocompleteInput
              name='user group'
              withNone
              disabled={notEditable || notEditable}
              title={`Custom cutoff ${cutoffOptions[selectedCutoffIdx]?.label || ''} user pool`}
              items={userGroupItems}
              onChange={(_, item) => onChangeUserGroup((item as Item).value as number)}
              value={{ label: usersGroupName, value: usersGroupId } as Item}
              onCreateItem={() => setCreateUserListOpen(role)}
              onEditItem={setEditUserListOpen}
            />
            {!notEditable && <$TabsContainer>
              <ConfirmAction
                title={allCutoffLocked ? 'Unlock all cutoff user roles' : 'Lock all cutoff users to role'} description=''
                onConfirm={() => 
                  onChangeCutoffLockAll(!allCutoffLocked,
                    userRows.map((r) => r.user), forcedRoles.map((r) => r.id), selectedCutoffId)}
              >
                <Switch 
                  name='lock'
                  title='Lock all cutoff users to role'
                  value={allCutoffLocked}
                  onChange={() => null}
                />
              </ConfirmAction>
            </$TabsContainer>}
          </>
        }
      </$TabsContainer>
      <Title alternate>
        Assigned users
      </Title>
    </$Container>
    <$ListsContainer container alignItems='flex-start'>
      <DataTable
        showEmptyImg={false}
        noFilters
        rows={rows}
        columns={columns}
      />
    </$ListsContainer>
    {(createUserListOpen || editingUserGroup) &&
      <NewModal open title={createUserListOpen ? 'User list creation' : 'Edit User List'} onClose={onClose}>
        <UsersListModal
          name={createUserListOpen ? '' : editingUserGroup?.name || ''}
          userList={createUserListOpen ? [] : editingUserGroup?.users || []}
          handleDelete={editingUserGroup ? () => handleDeleteUserGroup(editingUserGroup.id) : undefined}
          handleSave={(name: string, userIds: number[]) => 
            createUserListOpen ? handleCreateUserGroup(name, userIds) : 
              handleEditUserGroup(name, userIds, editingUserGroup?.id)}
        />
      </NewModal>
    }
    {(createDefaultUserListOpen || editingDefaultUserGroup) &&
      <NewModal open title={createDefaultUserListOpen ? 'Default User list creation' : 'Edit Default User List'} onClose={onClose}>
        <UsersListModal
          name={createDefaultUserListOpen ? '' : editingDefaultUserGroup?.name || ''}
          userList={createDefaultUserListOpen ? [] : editingDefaultUserGroup?.users || []}
          handleDelete={editingDefaultUserGroup ? () => handleDeleteUserGroup(editingDefaultUserGroup.id) : undefined}
          handleSave={(name: string, userIds: number[]) => 
            createDefaultUserListOpen ? handleCreateUserGroup(name, userIds, true) : 
              handleEditUserGroup(name, userIds, editingDefaultUserGroup?.id)}
        />
      </NewModal>
    }
    {(changeDefaultUserListOpen !== undefined) &&
       <DefaultRoleModal
         role={role}
         defaultUserList={changeDefaultUserListOpen}
         onSave={(changeEveryCutoff: boolean) => 
           onChangeDefaultUserGroup(changeDefaultUserListOpen as number, changeEveryCutoff)}
         onClose={() => setChangeDefaultUserListOpen(undefined)}
       />
    }
  </$ModalContainer>
}
